import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import US_STATES from "./data/usStates";
import BackHomeLink from "./BackHomeLink";

function shuffle(array) {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function buildOptions(state) {
  const wrongCapitals = shuffle(
    US_STATES.filter((s) => s.abbr !== state.abbr)
  )
    .slice(0, 3)
    .map((s) => s.capital);
  const options = shuffle([state.capital, ...wrongCapitals]);
  return { state, options, correctAnswer: state.capital };
}

function CapitalQuestion({ state, questionNumber, totalQuestions, timeout, onComplete }) {
  const [question] = useState(() => buildOptions(state));
  const [selected, setSelected] = useState(null);
  const [timer, setTimer] = useState(timeout);
  const [timedOut, setTimedOut] = useState(false);
  const answeredRef = useRef(false);

  useEffect(() => {
    if (answeredRef.current) return;
    if (timer <= 0) {
      answeredRef.current = true;
      setTimedOut(true);
      const id = setTimeout(() => {
        onComplete({
          state: question.state,
          userAnswer: null,
          correctAnswer: question.correctAnswer,
        });
      }, 1000);
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer, onComplete, question]);

  const handleSelect = (option) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    setSelected(option);
    setTimeout(() => {
      onComplete({
        state: question.state,
        userAnswer: option,
        correctAnswer: question.correctAnswer,
      });
    }, 600);
  };

  return (
    <div>
      <div
        style={{
          background: "none",
          boxShadow: "none",
          padding: 0,
          margin: "0 0 4px 0",
          minWidth: "unset",
          fontWeight: 600,
          color: "#888",
        }}
      >
        Question {questionNumber} of {totalQuestions}
      </div>
      <h3>What is the capital of {question.state.name}?</h3>
      <div
        style={{
          background: "none",
          boxShadow: "none",
          padding: 0,
          margin: 0,
          minWidth: "unset",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        {question.options.map((option) => {
          let background;
          if (selected || timedOut) {
            if (option === question.correctAnswer) background = "#4caf50";
            else if (option === selected) background = "#e53935";
          }
          return (
            <button
              key={option}
              onClick={() => handleSelect(option)}
              disabled={!!selected || timedOut}
              style={background ? { background } : undefined}
            >
              {option}
            </button>
          );
        })}
      </div>
      {timedOut ? (
        <div style={{ color: "red", marginTop: 8 }}>Time's up</div>
      ) : (
        <div>Time left: {timer}s</div>
      )}
    </div>
  );
}

function CapitalQuiz() {
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState(10);
  const [timeout, setTimeoutValue] = useState(10);
  const [current, setCurrent] = useState(0);
  const [results, setResults] = useState([]);
  const [quizStates, setQuizStates] = useState([]);

  const handleComplete = (result) => {
    setResults((prev) => [...prev, result]);
    setCurrent((c) => c + 1);
  };

  const handleStart = () => {
    const count = Math.min(
      Math.max(Number(questions) || 1, 1),
      US_STATES.length
    );
    setQuizStates(shuffle(US_STATES).slice(0, count));
    setStep(1);
  };

  if (step === 0) {
    return (
      <div>
        <BackHomeLink />
        <h2>US State Capital Quiz</h2>
        <label>
          Number of Questions:
          <input
            type="number"
            min={1}
            max={US_STATES.length}
            value={questions}
            onChange={(e) => setQuestions(Number(e.target.value))}
          />
        </label>
        <label>
          Timer per Question (seconds):
          <input
            type="number"
            min={3}
            value={timeout}
            onChange={(e) => setTimeoutValue(Number(e.target.value))}
          />
        </label>
        <button onClick={handleStart}>Start</button>
      </div>
    );
  }

  if (current < quizStates.length) {
    return (
      <CapitalQuestion
        key={current}
        state={quizStates[current]}
        questionNumber={current + 1}
        totalQuestions={quizStates.length}
        timeout={timeout}
        onComplete={handleComplete}
      />
    );
  }

  const correctCount = results.filter((r) => r.userAnswer === r.correctAnswer).length;
  const incorrectCount = results.length - correctCount;

  return (
    <div>
      <h2>Quiz Complete!</h2>
      <h3>Report:</h3>
      <ul>
        {results.map((r, i) => {
          const isCorrect = r.userAnswer === r.correctAnswer;
          return (
            <li key={i}>
              Q{i + 1}: Capital of {r.state.name} is {r.correctAnswer} |{" "}
              Your answer: {r.userAnswer || "No answer (timeout)"}{" "}
              {isCorrect ? (
                <span style={{ color: "green" }}>✔️</span>
              ) : (
                <span style={{ color: "red" }}>❌</span>
              )}
            </li>
          );
        })}
      </ul>
      <div>
        <strong>Correct answers:</strong> {correctCount}{" "}
        <span style={{ color: "green" }}>✔️</span>
        <br />
        <strong>Incorrect answers:</strong> {incorrectCount}{" "}
        <span style={{ color: "red" }}>❌</span>
        <br />
        <br />
        <button
          onClick={() => {
            setStep(0);
            setCurrent(0);
            setResults([]);
            setQuizStates([]);
          }}
        >
          Restart
        </button>
        &nbsp;
        <Link to="/" className="back-home-btn">
          &#8592; Home
        </Link>
      </div>
    </div>
  );
}

export default CapitalQuiz;
